<template>
  <div class="crystal-lattice-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$7大晶系 | SC, BCC, FCC$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <g transform="translate(200, 120) scale(1.5)">
             <!-- Lattice Lines (Simplified wireframe) -->
             <g stroke="rgba(255,255,255,0.15)" stroke-width="0.8" fill="none">
                <rect x="-30" y="-30" width="60" height="60" />
                <rect x="-30" y="-30" width="60" height="60" transform="translate(15, -15)" />
                <line x1="-30" y1="-30" x2="-15" y2="-45" />
                <line x1="30" y1="-30" x2="45" y2="-45" />
                <line x1="-30" y1="30" x2="-15" y2="15" />
                <line x1="30" y1="30" x2="45" y2="15" />
             </g>

             <!-- Atoms -->
             <g v-for="(atom, i) in currentAtoms" :key="i">
                <circle :cx="atom.x" :cy="atom.y" :r="radius" 
                        :fill="atom.z > 0 ? '#3b82f6' : '#06b6d4'" 
                        :opacity="0.7 + (atom.z + 30)/120" />
                <!-- Perspective shadow -->
                <circle :cx="atom.x" :cy="atom.y + 2" :r="radius" fill="rgba(0,0,0,0.3)" />
             </g>
          </g>
          <text x="20" y="30" fill="rgba(148, 163, 184, 0.5)" font-size="10" font-weight="700">3D 晶胞结构演示</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>晶体类型</label>
              <span class="val">{{ latticeType }}</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="mode-selector-row">
                 <span class="label">点阵类型：</span>
                 <el-radio-group v-model="latticeType" size="small" class="full-row-radio">
                    <el-radio-button label="SC">简单立方</el-radio-button>
                    <el-radio-button label="BCC">体心立方</el-radio-button>
                    <el-radio-button label="FCC">面心立方</el-radio-button>
                 </el-radio-group>
              </div>
              <div class="param-row">
                 <div class="s-item">
                    <label>原子半径</label>
                    <el-slider v-model="radius" :min="4" :max="18" size="small"></el-slider>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>晶体结构：</strong>晶格是物质固态排列的几何抽象。不同堆叠方式决定了材料的微观宏观性能。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrystalLatticeViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      latticeType: 'SC',
      radius: 8,
      angleX: 20,
      angleY: 30,
      timer: null,
      running: true
    }
  },
  computed: {
    currentAtoms() {
       let atoms = [];
       const s = 30;
       for (let x of [-s, s]) {
          for (let y of [-s, s]) {
             for (let z of [-s, s]) {
                atoms.push({x, y, z});
             }
          }
       }
       if (this.latticeType === 'BCC') atoms.push({x: 0, y: 0, z: 0});
       if (this.latticeType === 'FCC') {
          atoms.push({x: 0, y: s, z: 0}, {x: 0, y: -s, z: 0});
          atoms.push({x: s, y: 0, z: 0}, {x: -s, y: 0, z: 0});
          atoms.push({x: 0, y: 0, z: s}, {x: 0, y: 0, z: -s});
       }
       const radX = this.angleX * Math.PI / 180;
       const radY = this.angleY * Math.PI / 180;
       const cosX = Math.cos(radX), sinX = Math.sin(radX);
       const cosY = Math.cos(radY), sinY = Math.sin(radY);

       return atoms.map(p => {
          let y1 = p.y * cosX - p.z * sinX;
          let z1 = p.y * sinX + p.z * cosX;
          let x2 = p.x * cosY + z1 * sinY;
          let z2 = -p.x * sinY + z1 * cosY;
          return { x: x2, y: y1, z: z2 };
       }).sort((a,b) => a.z - b.z);
    }
  },
  methods: {
    animate() {
       if (!this.running) return;
       this.angleX += 0.5;
       this.angleY += 0.3;
       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() { 
    this.running = true;
    this.animate(); 
  },
  beforeDestroy() { 
    this.running = false;
    if (this.timer) cancelAnimationFrame(this.timer); 
  }
}
</script>

<style scoped lang="scss">
.crystal-lattice-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #0ea5e9; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .mode-selector-row { display: flex; align-items: center; gap: 10px; .label { font-size: 12px; color: #64748b; font-weight: 700; white-space: nowrap; } .full-row-radio { flex: 1; display: flex; justify-content: flex-start; } } .param-row { border-top: 1px solid #f1f5f9; padding-top: 8px; .s-item { label { font-size: 11px; color: #94a3b8; font-weight: 700; display: block; margin-bottom: 2px; } } } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
